<template>
    <!-- The blue info badge beside a title or setting: its text shows straight
         away on hover or keyboard focus. The browser's own title tooltip waits
         about a second and cannot be sped up, so this draws its own. -->
    <span   ref="badge"
            class="inline-flex shrink-0 cursor-help text-blue-600"
            tabindex="0"
            role="img"
            :aria-label="text"
            @mouseenter="open"
            @mouseleave="close"
            @focus="open"
            @blur="close"
            @keydown.esc="close"
    >
        <tabler-icon    name="infoCircleFilled"
                        :size-class="sizeClass"
        ></tabler-icon>

        <!-- On <body> so a card or slide-over that clips its overflow cannot
             cut it off. -->
        <teleport to="body">
            <div    v-if="show"
                    ref="bubble"
                    role="tooltip"
                    class="pointer-events-none fixed z-[100] w-max max-w-xs rounded-md bg-gray-900 px-3 py-2 text-left text-sm font-normal leading-5 text-white shadow-lg"
                    :style="position"
            >{{ text }}</div>
        </teleport>
    </span>
</template>

<script>
import TablerIcon from "./TablerIcon.vue"

// Gap between the badge and the bubble, and the margin kept from the window
// edge, in px.
const GAP = 8
const EDGE = 8

export default {
    name: "InfoTip",
    components: {TablerIcon},
    props: {
        text: {
            type: String,
            required: true,
        },
        sizeClass: {
            type: String,
            default: 'h-4 w-4',
        },
    },
    data() {
        return {
            show: false,
            position: {},
        }
    },
    beforeUnmount() {
        this.removeListeners()
    },
    methods: {
        open() {
            const rect = this.$refs.badge.getBoundingClientRect()
            // Below the badge, or above it in the bottom third of the window.
            const below = rect.bottom < window.innerHeight * (2 / 3)
            const vertical = below
                ? {top: `${rect.bottom + GAP}px`}
                : {bottom: `${window.innerHeight - rect.top + GAP}px`}

            // Drawn off screen first so its real width can be measured, then
            // centred on the badge and kept inside the window.
            this.position = {...vertical, left: '-9999px'}
            this.show = true

            this.$nextTick(() => {
                if (! this.$refs.bubble) {
                    return
                }
                const width = this.$refs.bubble.offsetWidth
                const left = Math.min(
                    Math.max(EDGE, rect.left + (rect.width / 2) - (width / 2)),
                    window.innerWidth - width - EDGE,
                )
                this.position = {...vertical, left: `${left}px`}
            })

            // A fixed bubble would drift from its badge on scroll, so hide it.
            window.addEventListener('scroll', this.close, true)
            window.addEventListener('resize', this.close)
        },
        close() {
            this.show = false
            this.removeListeners()
        },
        removeListeners() {
            window.removeEventListener('scroll', this.close, true)
            window.removeEventListener('resize', this.close)
        },
    },
}
</script>
